// Activity rail: the app's top-level navigation. On desktop it is a vertical
// icon rail (leftmost column); below 768px it collapses into a fixed bottom tab
// bar. Search / Bulk Add / Settings are navigable destinations; Logout is a
// trailing action shown only on desktop (on mobile it lives in the Settings page).
import { PmsTooltip } from '@/components/ui/pms-tooltip';
import { useBulkReadyCount } from '@/lib/bulk-add/bulk-session';
import { useLogout } from '@/lib/use-logout';
import { Link, useRouterState } from '@tanstack/react-router';

type RailDest = {
  to: string;
  icon: string;
  label: string;
  /** Active when the current pathname starts with one of these prefixes. */
  match: (pathname: string) => boolean;
};

// Search owns both the list (/words) and any open word (/words/$wordId); the
// other two own their single route. Order longest-prefix-first is not needed
// because the matchers are mutually exclusive.
const TOP_DESTS: RailDest[] = [
  {
    to: '/words',
    icon: 'search',
    label: 'Recherche',
    match: (p) => p === '/words' || /^\/words\/(?!bulk-add$|settings$)/.test(p),
  },
  {
    to: '/words/bulk-add',
    icon: 'library_add',
    label: 'Ajout en masse',
    match: (p) => p === '/words/bulk-add',
  },
];

const SETTINGS_DEST: RailDest = {
  to: '/words/settings',
  icon: 'settings',
  label: 'Réglages',
  match: (p) => p === '/words/settings',
};

/** Top-level navigation rail (desktop) / bottom tab bar (mobile). */
export function WordRail() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { handleLogout, pending } = useLogout();
  // Count of ready-but-unopened bulk-add reviews — badged on the bulk-add item.
  const readyCount = useBulkReadyCount();

  function renderDest(dest: RailDest) {
    const active = dest.match(pathname);
    const badge = dest.to === '/words/bulk-add' && readyCount > 0 ? readyCount : null;
    return (
      <PmsTooltip key={dest.to} label={dest.label} side="right">
        <Link
          to={dest.to}
          className={`word-rail__item${active ? ' is-active' : ''}`}
          aria-label={badge ? `${dest.label} (${badge} prête${badge > 1 ? 's' : ''})` : dest.label}
          aria-current={active ? 'page' : undefined}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            {dest.icon}
          </span>
          {badge && (
            <span className="word-rail__badge" aria-hidden="true">
              {badge}
            </span>
          )}
        </Link>
      </PmsTooltip>
    );
  }

  return (
    <nav className="word-rail" aria-label="Navigation principale">
      <div className="word-rail__group word-rail__group--top">{TOP_DESTS.map(renderDest)}</div>

      <div className="word-rail__spacer" aria-hidden="true" />

      <div className="word-rail__group word-rail__group--bottom">
        {renderDest(SETTINGS_DEST)}
        <PmsTooltip label="Se déconnecter" side="right">
          <button
            type="button"
            className="word-rail__item word-rail__logout"
            aria-label="Se déconnecter"
            disabled={pending}
            onClick={handleLogout}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              logout
            </span>
          </button>
        </PmsTooltip>
      </div>
    </nav>
  );
}
